import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useLanguage } from '../../../../../../API/Context/LanguageContext';
import { useHealthBackgroundActions } from '../../../../../../API/Hooks/useHealthBackgroundActions';
import EnumSelect from '../../../../LoggedUserGeneral/ProfileOnboarding/Steps/Widgets/EnumSelect';
import { FAMILY_RELATIONSHIP_MAP } from '../../../../../../ConfigFiles/maps/hereditary_relationship_map';
import { FamilyRelationshipEnum } from '../../../../../../Types/Enums/HereditaryRelationshipEnum';

export interface AddFamilyMemberModalProps {
  show: boolean;
  onHide: () => void;
}

export const AddFamilyMemberModal: React.FC<AddFamilyMemberModalProps> = ({ show, onHide }) => {
  const { lan } = useLanguage();
  const currentLanKey = (lan as number) === 0 ? 0 : 1;
  const { addFamilyMember, loading } = useHealthBackgroundActions();

  const [relationship, setRelationship] = useState<FamilyRelationshipEnum>(FamilyRelationshipEnum.MOTHER);
  const [displayName, setDisplayName] = useState('');
  const [isDeceased, setIsDeceased] = useState(false);
  const [causeOfDeath, setCauseOfDeath] = useState('');
  const [ageAtDeath, setAgeAtDeath] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      relationship,
      display_name: displayName.trim() || null,
      is_deceased: isDeceased || null,
      cause_of_death: isDeceased && causeOfDeath.trim() ? causeOfDeath.trim() : null,
      age_at_death: isDeceased && ageAtDeath ? Number(ageAtDeath) : null,
    };

    const res = await addFamilyMember(payload as any);

    if (res) {
      setRelationship(FamilyRelationshipEnum.MOTHER);
      setDisplayName('');
      setIsDeceased(false);
      setCauseOfDeath('');
      setAgeAtDeath('');
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{currentLanKey === 0 ? 'Agregar Familiar' : 'Add Family Member'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="d-flex flex-column gap-3">
          <Form.Group>
            <Form.Label className="fw-semibold">{currentLanKey === 0 ? 'Parentesco' : 'Relationship'}</Form.Label>
            <EnumSelect
              value={relationship}
              options={FAMILY_RELATIONSHIP_MAP}
              onChange={(val) => setRelationship(val as FamilyRelationshipEnum)}
              required={true}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="fw-semibold">
              {currentLanKey === 0 ? 'Nombre / Alias (Opcional)' : 'Display Name / Alias (Optional)'}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={currentLanKey === 0 ? 'Ej. Abuela María, Tío Juan...' : 'e.g. Grandma Mary, Uncle John...'}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </Form.Group>

          <Form.Check
            type="checkbox"
            id="is-deceased-checkbox"
            label={currentLanKey === 0 ? '¿El familiar ha fallecido?' : 'Is the relative deceased?'}
            checked={isDeceased}
            onChange={(e) => setIsDeceased(e.target.checked)}
          />

          {isDeceased && (
            <div className="row g-2 ps-3 border-start border-3 border-danger">
              <div className="col-md-7">
                <Form.Group>
                  <Form.Label className="small fw-semibold">{currentLanKey === 0 ? 'Causa de Muerte' : 'Cause of Death'}</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder={currentLanKey === 0 ? 'Ej. Infarto, Cáncer...' : 'e.g. Heart attack, Cancer...'}
                    value={causeOfDeath}
                    onChange={(e) => setCauseOfDeath(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="col-md-5">
                <Form.Group>
                  <Form.Label className="small fw-semibold">{currentLanKey === 0 ? 'Edad al Fallecer' : 'Age at Death'}</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={0}
                    max={120}
                    placeholder="e.g. 72"
                    value={ageAtDeath}
                    onChange={(e) => setAgeAtDeath(e.target.value)}
                  />
                </Form.Group>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {currentLanKey === 0 ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {currentLanKey === 0 ? 'Guardar Familiar' : 'Save Member'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};